import React, { useState } from 'react'
import { Popover, Button, Modal, Input, App } from 'antd'
import {
  MoreHorizontal,
  Palette,
  PencilLine,
  Copy,
  Link2,
  FileDown,
  Archive,
  Trash2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { MindmapService } from '../../../../../services/mindmap/mindmapService'
import { useProfileContext } from '../../../../../context/profile'
import { useMindMapContext } from '../../../../../context/mindmap'
import { StyleSelectorModal } from '../../../../StyleSelector/StyleSelectorModal'
import { StyledButtonWrapper } from '../../styles/MindMapHeaderStyles'
import { MobileTooltip } from './MobileTooltip'
import { TYPOGRAPHY } from '../../../../../styles/theme'

// --- Styled components ---

const MenuContainer = styled.div`
  width: 220px;
  padding: 6px;
  background: var(--color-bg-elevated);
`

const MenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: ${({ $danger }) =>
    $danger ? 'var(--color-error, #ff4d4f)' : 'var(--color-text-primary)'};
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: ${({ $danger }) => ($danger ? 'rgba(255, 77, 79, 0.08)' : 'var(--color-bg-hover)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const MenuItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const MenuDivider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
`

// --- Component ---

interface ProjectSettingsMenuProps {
  mindmapId: string
  mindmapTitle: string
  mindmapImageStyle: string | null
  onTitleChange?: (title: string) => void
  onImageStyleChange?: (style: string | null) => void
  onShareClick?: () => void
  onExportClick?: () => void
}

export const ProjectSettingsMenu: React.FC<ProjectSettingsMenuProps> = ({
  mindmapId,
  mindmapTitle,
  mindmapImageStyle,
  onTitleChange,
  onImageStyleChange,
  onShareClick,
  onExportClick,
}) => {
  const { notification, modal } = App.useApp()
  const navigate = useNavigate()
  const { profile, updateProfile } = useProfileContext()
  const { setNodes } = useMindMapContext()

  const [menuOpen, setMenuOpen] = useState(false)
  const [styleModalOpen, setStyleModalOpen] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [renameTitle, setRenameTitle] = useState('')
  const [renameSaving, setRenameSaving] = useState(false)
  const [duplicating, setDuplicating] = useState(false)

  const handleStyleChange = async (styleId: string | null): Promise<void> => {
    onImageStyleChange?.(styleId)
    await MindmapService.update(mindmapId, { image_style: styleId })
    notification.success({
      title: 'Style updated',
      description: 'Regenerate portraits and storyboards to apply the new look.',
      duration: 4,
    })
  }

  const handleSetAsDefault = async (styleId: string): Promise<void> => {
    const { error } = await updateProfile({ default_image_style: styleId })
    if (error) {
      notification.error({ title: 'Failed to update default style', description: error.message })
    } else {
      notification.success({
        title: 'Default style updated',
        description: 'New projects will use this style unless overridden.',
        duration: 3,
      })
    }
  }

  const handleOpenRename = (): void => {
    setMenuOpen(false)
    setRenameTitle(mindmapTitle)
    setRenameModalOpen(true)
  }

  const handleSaveRename = async (): Promise<void> => {
    if (!renameTitle.trim()) return
    setRenameSaving(true)
    const trimmed = renameTitle.trim()
    const { error } = await MindmapService.update(mindmapId, { title: trimmed })
    setRenameSaving(false)
    if (error) {
      notification.error({ title: 'Failed to rename project', description: error.message })
    } else {
      onTitleChange?.(trimmed)
      setNodes((nodes) =>
        nodes.map((node) =>
          node.data.category === 'venture'
            ? { ...node, data: { ...node.data, companyName: trimmed, userModified: true } }
            : node
        )
      )
      setRenameModalOpen(false)
      notification.success({ title: 'Project renamed' })
    }
  }

  const handleDuplicate = async (): Promise<void> => {
    setMenuOpen(false)
    setDuplicating(true)
    const { data, error } = await MindmapService.duplicate(mindmapId)
    setDuplicating(false)
    if (error) {
      notification.error({ title: 'Failed to duplicate project', description: error.message })
    } else if (data) {
      notification.success({ title: 'Project duplicated' })
      void navigate(`/mindmap/${data.id}`)
    }
  }

  const handleArchive = (): void => {
    setMenuOpen(false)
    modal.confirm({
      title: 'Archive project',
      content: `Archive "${mindmapTitle}"? You can restore it from the dashboard.`,
      okText: 'Archive',
      cancelText: 'Cancel',
      onOk: async () => {
        const { error } = await MindmapService.update(mindmapId, { is_archived: true })
        if (error) {
          notification.error({ title: 'Failed to archive project', description: error.message })
        } else {
          notification.success({ title: 'Project archived' })
          void navigate('/')
        }
      },
    })
  }

  const handleDelete = (): void => {
    setMenuOpen(false)
    modal.confirm({
      title: 'Delete project',
      icon: null,
      content: `Are you sure you want to delete "${mindmapTitle}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const { error } = await MindmapService.delete(mindmapId)
        if (error) {
          notification.error({ title: 'Failed to delete project', description: error.message })
        } else {
          notification.success({ title: 'Project deleted' })
          void navigate('/')
        }
      },
    })
  }

  return (
    <>
      <MobileTooltip title="More options">
        <Popover
          content={
            <MenuContainer>
              <MenuItem
                onClick={() => {
                  setMenuOpen(false)
                  setStyleModalOpen(true)
                }}
              >
                <MenuItemLeft>
                  <Palette size={15} />
                  Image Style
                </MenuItemLeft>
              </MenuItem>
              <MenuItem onClick={handleOpenRename}>
                <MenuItemLeft>
                  <PencilLine size={15} />
                  Rename Project
                </MenuItemLeft>
              </MenuItem>
              <MenuItem onClick={handleDuplicate} disabled={duplicating}>
                <MenuItemLeft>
                  <Copy size={15} />
                  {duplicating ? 'Duplicating...' : 'Duplicate Project'}
                </MenuItemLeft>
              </MenuItem>
              {onShareClick && (
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false)
                    onShareClick()
                  }}
                >
                  <MenuItemLeft>
                    <Link2 size={15} />
                    Share
                  </MenuItemLeft>
                </MenuItem>
              )}
              {onExportClick && (
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false)
                    onExportClick()
                  }}
                >
                  <MenuItemLeft>
                    <FileDown size={15} />
                    Export
                  </MenuItemLeft>
                </MenuItem>
              )}
              <MenuDivider />
              <MenuItem onClick={handleArchive}>
                <MenuItemLeft>
                  <Archive size={15} />
                  Archive Project
                </MenuItemLeft>
              </MenuItem>
              <MenuItem $danger onClick={handleDelete}>
                <MenuItemLeft>
                  <Trash2 size={15} />
                  Delete Project
                </MenuItemLeft>
              </MenuItem>
            </MenuContainer>
          }
          trigger="click"
          placement="bottomRight"
          open={menuOpen}
          onOpenChange={setMenuOpen}
          styles={{ body: { padding: 0 } }}
        >
          <StyledButtonWrapper>
            <Button type="default" icon={<MoreHorizontal size={16} />} />
          </StyledButtonWrapper>
        </Popover>
      </MobileTooltip>

      <Modal
        title="Rename project"
        open={renameModalOpen}
        onOk={handleSaveRename}
        onCancel={() => setRenameModalOpen(false)}
        okText="Save"
        okButtonProps={{ loading: renameSaving, disabled: !renameTitle.trim() }}
      >
        <Input
          value={renameTitle}
          onChange={(e) => setRenameTitle(e.target.value)}
          placeholder="Enter project name"
          onPressEnter={handleSaveRename}
          autoFocus
        />
      </Modal>

      <StyleSelectorModal
        open={styleModalOpen}
        onClose={() => setStyleModalOpen(false)}
        value={mindmapImageStyle ?? profile?.default_image_style ?? 'realistic-photo'}
        onChange={(id) => handleStyleChange(id)}
        defaultStyleId={profile?.default_image_style ?? 'realistic-photo'}
        onSetAsDefault={handleSetAsDefault}
      />
    </>
  )
}
